import {EventEmitter} from "@angular/core";
import {PageEvent} from "@angular/material/paginator";
import {SortDirection} from "@angular/material/sort";

import {Lookup, PluralizedValues, UtilsService} from "framework/services/utils.service";
import {DatatableColumnDirective} from "framework/ui/datatables/datatable.component";
import {ViewState} from "framework/ui/navigation/view-state.service";
import {Future} from "framework/utils/concurrency";

export abstract class DatatableManagerBase<K, T>
{
    loading: boolean = false;

    config = {
        messages: {
            emptyMessage  : "Loading...",
            totalMessage  : "total",
            warningMessage: ""
        }
    };

    protected m_rowGenerationError: string;
    get rowGenerationError(): string
    {
        return this.m_rowGenerationError;
    }

    protected m_rows: T[] = [];
    get rows(): T[]
    {
        return this.m_rows;
    }

    detectChanges = new EventEmitter<void>();

    //--//

    public static readonly ROW_RENDER_RETRIES: number = 2;
    private readonly m_rowCountVerifier               = new RowCountVerifier(DatatableManager.ROW_RENDER_RETRIES);

    protected m_all: K[] = [];

    protected m_offset: number;
    protected m_count           = 0;
    protected m_listInvalidated = true;
    protected m_pageInvalidated = true;
    protected m_fetchingData    = false;

    get count(): number
    {
        return this.m_count;
    }

    get offset(): number
    {
        if (this.m_offset === undefined)
        {
            this.m_offset = this.getViewState("TABLE_OFFSET", 0);
        }

        return this.m_offset;
    }

    set offset(value: number)
    {
        this.m_offset = value;

        this.setViewState("TABLE_OFFSET", value);
    }

    //--//

    public defaultLimit = 10;

    private m_limit: number;

    get limit(): number
    {
        if (this.m_limit === undefined)
        {
            this.m_limit = Math.max(10, this.getViewState("TABLE_LIMIT", this.defaultLimit));
        }

        return this.m_limit;
    }

    set limit(value: number)
    {
        if (value > 0)
        {
            this.m_limit = value;

            this.setViewState("TABLE_LIMIT", value);
        }
    }

    //--//

    private m_sort: DatatableSort[];

    get sort(): DatatableSort[]
    {
        if (!this.m_sort)
        {
            this.m_sort = this.getViewState<DatatableSort[]>("TABLE_SORT");
        }

        return this.m_sort;
    }

    set sort(value: DatatableSort[])
    {
        this.m_sort = value;

        let oldSort = this.getViewState<DatatableSort[]>("TABLE_SORT");
        if (!UtilsService.compareJson(oldSort, value))
        {
            if (this.resetOffsetOnSort) this.offset = 0;
        }

        this.setViewState("TABLE_SORT", value);
    }

    //--//

    private m_suspendRefresh: Promise<void>;

    //--//

    constructor(protected provider: IDatatableDataProviderBase<T>,
                private viewStateAccessor: () => ViewState,
                protected readonly resetOffsetOnSort: boolean = true)
    {
        const pluralized                  = this.getPluralizedValues();
        this.config.messages.emptyMessage = `Loading ${pluralized.multipleFormat}...`;
    }

    getPluralizedValues(): PluralizedValues
    {
        return this.provider.getPluralizedItem() || new PluralizedValues("Items", "Item", "Items");
    }

    protected abstract refreshDataInner(): Promise<void>;

    protected abstract exportDataInner(): Promise<T[]>;

    public abstract slice(offset: number,
                          limit: number): Promise<K[]>;

    private setViewState(key: string,
                         value: any)
    {
        let viewState = this.viewStateAccessor();
        if (viewState) viewState.set(key, value, true);
    }

    private getViewState<T>(key: string,
                            defaultValue: T = null): T
    {
        let viewState = this.viewStateAccessor();
        return (viewState && viewState.get(key, defaultValue)) || defaultValue;
    }

    getTableConfigId(): string
    {
        return this.provider.getTableConfigId();
    }

    getConfigs(): Promise<ColumnConfiguration[]>
    {
        return this.provider.getColumnConfigs();
    }

    async setConfigs(configs: ColumnConfiguration[]): Promise<boolean>
    {
        return this.provider.setColumnConfigs(configs);
    }

    private m_selectionManager: DatatableSelectionManager<K, T, any>;

    set selectionManager(selectionManager: DatatableSelectionManager<K, T, any>)
    {
        this.m_selectionManager = selectionManager;
    }

    get selectionManager(): DatatableSelectionManager<K, T, any>
    {
        return this.m_selectionManager;
    }

    enableSimpleSelection<ID>(keyConverter: (key: K) => ID,
                              valueConverter: (value: T) => ID,
                              allowSelection?: (value: ID) => boolean,
                              single        = false,
                              showSelectAll = true): SimpleSelectionManager<K, T, ID>
    {
        let manager           = new SimpleSelectionManager(this, single, showSelectAll, keyConverter, valueConverter, allowSelection);
        this.selectionManager = manager;
        return manager;
    }

    disableSimpleSelection()
    {
        this.selectionManager = null;
    }

    private m_expansionManager: DatatableExpansionManager<K, T, any>;

    protected set expansionManager(expansionManager: DatatableExpansionManager<K, T, any>)
    {
        this.m_expansionManager = expansionManager;
    }

    get expansionManager(): DatatableExpansionManager<K, T, any>
    {
        return this.m_expansionManager;
    }

    enableSimpleExpansion<ID>(keyConverter: (key: K) => ID,
                              valueConverter: (value: T) => ID,
                              single        = false,
                              showExpandAll = true): SimpleExpansionManager<K, T, ID>
    {
        let manager           = new SimpleExpansionManager(this, single, showExpandAll, keyConverter, valueConverter);
        this.expansionManager = manager;
        return manager;
    }

    public reset(): void
    {
        this.m_rows = [];
    }

    // Replaces a single row and hands the datatable a new array reference so mat-table re-renders;
    // an in-place mutation of m_rows keeps the same reference and would not trigger a render.
    public replaceRow(oldRow: T,
                      newRow: T): void
    {
        const index = this.m_rows.indexOf(oldRow);
        if (index < 0) return;

        const rows  = [...this.m_rows];
        rows[index] = newRow;
        this.postProcessRows(rows);
        this.m_rows = rows;
        this.detectChanges.emit();
    }

    protected listInvalidated()
    {
        this.m_listInvalidated = true;
        this.pageInvalidated();
    }

    protected pageInvalidated()
    {
        this.m_pageInvalidated = true;
    }

    protected async listUpdated()
    {
        if (this.selectionManager) await this.selectionManager.update();
        if (this.expansionManager) await this.expansionManager.update();

        const pluralized                  = this.getPluralizedValues();
        this.config.messages.emptyMessage = this.m_rowGenerationError || `No ${pluralized.zeroFormat} to display.`;
        this.config.messages.totalMessage = `Total ${UtilsService.pluralize(this.count, pluralized)}`;
        this.detectChanges.emit();
    }

    protected postProcessRows(rows: T[])
    {
        let start = this.offset * this.limit;
        for (let r = 0; r < rows.length; r++)
        {
            (<DatatableRow<T>>rows[r]).rowNumber = start + r + 1;
        }
    }

    async refreshData()
    {
        // Invalidate the data in flight.
        this.listInvalidated();

        await this.refreshDataInner();
    }

    async exportData(): Promise<T[]>
    {
        return await this.exportDataInner();
    }

    /**
     * returns rows, as is, if there are expected num
     * returns null if not expected and should retry
     * returns [] if there is not expected and should not retry
     * @param rows
     * @protected
     */
    protected hasExpectedNumRows<R>(rows: R[]): R[]
    {
        const expected   = Math.min(this.count - (this.offset * this.limit), this.limit);
        const resultRows = this.m_rowCountVerifier.verifyRowCount(rows, expected);
        if (resultRows != rows && resultRows?.length == 0)
        {
            this.m_rowGenerationError = "Something went wrong when loading the data.";
        }
        return resultRows;
    }

    protected clearList()
    {
        this.m_all   = [];
        this.m_count = this.m_all.length;
        this.listUpdated();
    }

    async onPage(event: PageEvent)
    {
        this.limit  = event.pageSize;
        this.offset = event.pageIndex;

        await this.refreshPage();
    }

    async refreshPage()
    {
        // Invalidate the page in flight.
        this.pageInvalidated();

        await this.refreshDataInner();
    }

    onSort(sort: DatatableSort[])
    {
        this.sort = sort;
        this.refreshData();
    }

    rowClicked(columnId: string,
               row: T)
    {
        this.provider.itemClicked(columnId, row);
    }

    get isRefreshDisabled(): boolean
    {
        return !!this.m_suspendRefresh;
    }

    async disableRefreshWhileProcessing(callback: () => Promise<void>)
    {
        await this.waitForSuspension();

        this.m_suspendRefresh = this.disableRefreshWhileProcessingInner(callback);

        await this.waitForSuspension();
        await this.refreshData();
    }

    private async disableRefreshWhileProcessingInner(callback: () => Promise<void>)
    {
        try
        {
            await callback();
        }
        catch (e)
        {
            // Sink all errors.
        }

        // Add a little delay, to allow database change notifications to trickle through.
        await Future.delayed(100);
    }

    private async waitForSuspension()
    {
        let suspension = this.m_suspendRefresh;
        while (suspension)
        {
            await suspension;

            if (suspension == this.m_suspendRefresh)
            {
                // Only reset if it hasn't changed.
                this.m_suspendRefresh = null;
                break;
            }

            suspension = this.m_suspendRefresh;
        }
    }
}

type DatatableRow<T> = T & { rowNumber: number };

class RowCountVerifier
{
    private prevActual: number;
    private prevActualCt: number;

    constructor(public readonly numRetries: number)
    {}

    public verifyRowCount<R>(rows: R[],
                             expected: number): R[]
    {
        const actual = rows?.length || 0;
        if (expected == actual)
        {
            this.prevActualCt = 0;
            return rows;
        }

        if (actual == this.prevActual)
        {
            if (++this.prevActualCt > this.numRetries)
            {
                return [];
            }
            else
            {
                return null;
            }
        }

        this.prevActual   = actual;
        this.prevActualCt = 1;
        return null;
    }
}

export class DatatableManager<K, E, T> extends DatatableManagerBase<K, T>
{
    constructor(protected providerTyped: IDatatableDataProvider<K, E, T>,
                viewStateAccessor: () => ViewState,
                resetOffsetOnSort: boolean = true)
    {
        super(providerTyped, viewStateAccessor, resetOffsetOnSort);
    }

    public slicePage(offset: number,
                     limit: number): Promise<K[]>
    {
        return this.slice(offset * limit, limit);
    }

    public async slice(offset: number,
                       limit: number): Promise<K[]>
    {
        const length = this.count;
        const start  = offset;
        const end    = Math.min(start + limit, length);

        let keys: K[] = [];

        for (let i = start; i < end; i++)
        {
            let key = this.m_all[i];
            if (key) keys.push(key);
        }

        return keys;
    }

    protected async exportDataInner()
    {
        // Get all row IDs to get a count
        const ids = await this.providerTyped.getList();

        // Get all row data
        const rowsRaw = await this.providerTyped.getPage(0, ids.length || 0);
        const rows    = await this.providerTyped.transform(rowsRaw);

        // Return transformed row data
        return rows;
    }

    protected async refreshDataInner()
    {
        if (this.isRefreshDisabled) return;

        if (!this.m_fetchingData)
        {
            this.m_fetchingData = true;

            // We delay a bit to let all the filters and configurations to be applied to the table.
            await Future.delayed(50);

            // Only show the loading indicator if it takes more than half a second.
            let timer = setTimeout(() =>
                                   {
                                       this.loading = true;
                                       this.detectChanges.emit();
                                   }, 500);

            try
            {
                //
                // Other activities could interleave with refreshData and refreshPage.
                // We use a counter to ensure we only refresh the page if we have rows from the current list.
                //
                while (this.m_listInvalidated || this.m_pageInvalidated)
                {
                    if (this.providerTyped.wasDestroyed()) return;

                    if (this.m_listInvalidated)
                    {
                        this.m_listInvalidated = false;

                        //--//

                        this.m_all   = await this.providerTyped.getList() || [];
                        this.m_count = this.m_all.length;
                        if (this.m_listInvalidated)
                        {
                            await Future.delayed(50);
                            continue; // Data has changed, retry.
                        }

                        await this.listUpdated();
                    }

                    if (this.m_pageInvalidated)
                    {
                        this.m_pageInvalidated = false;

                        //--//

                        // Make sure we always stay inside the range of available entries.
                        const maxOffset = this.count > 0 ? Math.ceil(this.count / this.limit) - 1 : 0;
                        let offset      = Math.min(this.offset, maxOffset);
                        if (this.offset != offset)
                        {
                            this.offset = offset;
                            this.detectChanges.emit();
                        }

                        let rowsRaw = await this.providerTyped.getPage(this.offset, this.limit);
                        rowsRaw     = this.hasExpectedNumRows(rowsRaw);
                        if (!rowsRaw)
                        {
                            this.listInvalidated();
                            await Future.delayed(50);
                            continue; // Data has changed, retry.
                        }
                        if (!rowsRaw.length) this.clearList();

                        let rows = await this.providerTyped.transform(rowsRaw);

                        if (this.m_listInvalidated || this.m_pageInvalidated) continue; // Data has changed, retry.

                        this.postProcessRows(rows);

                        this.m_rows = rows;
                    }
                }
            }
            catch (e)
            {
                // Ignore errors.
                console.error(e);
            }
            finally
            {
                clearTimeout(timer);

                this.m_fetchingData = false;

                this.loading = false;
                this.detectChanges.emit();
            }
        }
    }
}

type GroupableRow<T> = T & { rowSpan: Lookup<number> };

export class GroupingDatatableManager<K, E, T, G = keyof T> extends DatatableManager<K, E, T>
{
    constructor(protected providerTyped: IDatatableGroupingProvider<K, E, T, G>,
                viewStateAccessor: () => ViewState,
                resetOffsetOnSort: boolean = false)
    {
        super(providerTyped, viewStateAccessor, resetOffsetOnSort);
    }

    public sortRowsByGroupBy(rows: T[])
    {
        if (rows?.length)
        {
            const groupByColumns = this.providerTyped.groupByColumns() || [];
            rows.sort((a,
                       b) =>
                      {
                          for (const groupByCol of groupByColumns)
                          {
                              const comparison = this.providerTyped.compareByGroupBy(a, b, groupByCol.groupBy, groupByCol.ascending);
                              if (comparison) return comparison;
                          }
                          return 0;
                      });
        }
    }

    public getRowSpan(row: T,
                      colId: string): number
    {
        return (<GroupableRow<T>>row).rowSpan[colId];
    }

    protected postProcessRows(rows: T[])
    {
        super.postProcessRows(rows);

        const groupableRows = <GroupableRow<T>[]>rows || [];
        for (const row of groupableRows) row.rowSpan = {};

        const groupByColumns = this.providerTyped.groupByColumns();
        if (groupByColumns?.length) this.assignRowSpans(groupableRows, 0, groupableRows.length, groupByColumns, 0);
    }

    private assignRowSpans(rows: GroupableRow<T>[],
                           rowIdxLow: number,
                           rowIdxHigh: number,
                           groupByCols: GroupByColumnDefinition<G>[],
                           colIdx: number)
    {
        const groupBy = groupByCols[colIdx];
        if (groupBy)
        {
            let firstOfGroup = rows[rowIdxLow];
            for (let i = rowIdxLow + 1; i < rowIdxHigh; i++)
            {
                const row = rows[i];
                if (this.providerTyped.compareByGroupBy(firstOfGroup, row, groupBy.groupBy, true))
                {
                    this.assignRowSpansHelper(rows, rowIdxLow, i, groupByCols, colIdx);

                    rowIdxLow    = i;
                    firstOfGroup = row;
                }
            }

            this.assignRowSpansHelper(rows, rowIdxLow, rowIdxHigh, groupByCols, colIdx);
        }
    }

    private assignRowSpansHelper(rows: GroupableRow<T>[],
                                 rowIdxLow: number,
                                 rowIdxHigh: number,
                                 groupByCols: GroupByColumnDefinition<G>[],
                                 colIdx: number)
    {
        const colId = groupByCols[colIdx].id;

        if (rows.length)
        {
            rows[rowIdxLow].rowSpan[colId] = rowIdxHigh - rowIdxLow;
            for (let i = rowIdxLow + 1; i < rowIdxHigh; i++) rows[i].rowSpan[colId] = 0;
            this.assignRowSpans(rows, rowIdxLow, rowIdxHigh, groupByCols, colIdx + 1);
        }
    }

    public enableSimpleExpansion(): null {return null;}

    public enableSimpleSelection(): null {return null;}
}

export class LazyListDatatableManager<K, E, T> extends DatatableManagerBase<K, T>
{
    constructor(private providerTyped: ILazyListDatatableProvider<K, E, T>,
                viewStateAccessor: () => ViewState,
                private m_batchSize = 200)
    {
        super(providerTyped, viewStateAccessor);
    }

    enableSimpleSelection<ID>(keyConverter: (key: K) => ID,
                              valueConverter: (value: T) => ID,
                              allowSelection?: (value: ID) => boolean,
                              single        = false,
                              showSelectAll = true,
                              filterSelectableItems?: (allIds: ID[]) => Promise<ID[]>): LazySimpleSelectionManager<K, T, ID>
    {
        // select all doesn't make sense when only one is present
        if ((!filterSelectableItems || !allowSelection) && (filterSelectableItems || allowSelection)) showSelectAll = false;

        let manager           = new LazySimpleSelectionManager(this, single, showSelectAll, keyConverter, valueConverter, filterSelectableItems, allowSelection);
        this.selectionManager = manager;
        return manager;
    }

    protected async exportDataInner()
    {
        const rowsRaw = await this.getAll();
        const rows    = await this.providerTyped.transform(rowsRaw);

        // Return the transformed row data
        return rows;
    }

    disableSimpleSelection()
    {
        this.selectionManager = null;
    }

    protected async refreshDataInner()
    {
        if (this.isRefreshDisabled) return;

        if (!this.m_fetchingData)
        {
            this.m_fetchingData = true;

            // We delay a bit to let all the filters and configurations to be applied to the table.
            await Future.delayed(50);

            // Only show the loading indicator if it takes more than half a second.
            let timer = setTimeout(() =>
                                   {
                                       this.loading = true;
                                       this.detectChanges.emit();
                                   }, 500);

            try
            {
                //
                // Other activities could interleave with refreshData and refreshPage.
                // We use a counter to ensure we only refresh the page if we have rows from the current list.
                //
                while (this.m_listInvalidated || this.m_pageInvalidated)
                {
                    if (this.provider.wasDestroyed()) return;

                    const wasListInvalidated = this.m_listInvalidated;

                    if (wasListInvalidated)
                    {
                        this.m_listInvalidated = false;
                        this.m_all             = [];
                        // Fetch first page to get the count
                        await this.getIds(0, this.limit, 0);

                        if (this.m_listInvalidated) continue; // Data has changed, retry.
                    }

                    if (this.m_pageInvalidated)
                    {
                        this.m_pageInvalidated = false;

                        //--//

                        // Make sure we always stay inside the range of available entries.
                        const maxOffset = this.count > 0 ? Math.ceil(this.count / this.limit) - 1 : 0;
                        let offset      = Math.min(this.offset, maxOffset);
                        if (this.offset != offset)
                        {
                            this.offset = offset;
                            this.detectChanges.emit();
                        }

                        let ids = await this.getIds(this.offset * this.limit, this.limit, this.count);

                        ids = this.hasExpectedNumRows(ids);
                        if (this.count > 0 && !ids)
                        {
                            this.listInvalidated();
                            await Future.delayed(50);
                            continue; // Data has changed, retry.
                        }
                        if (!ids.length) this.clearList();

                        let batch             = await this.providerTyped.getBatch(ids);
                        let intermediate: E[] = [];
                        for (let key of ids)
                        {
                            const value = batch.get(key);
                            if (value)
                            {
                                intermediate.push(batch.get(key));
                            }
                        }

                        let rows = await this.providerTyped.transform(intermediate);

                        if (this.m_pageInvalidated) continue; // Data has changed, retry.

                        this.postProcessRows(rows);

                        this.m_rows = rows;
                        this.detectChanges.emit();
                    }

                    if (wasListInvalidated) await this.listUpdated();
                }
            }
            catch (e)
            {
                // Ignore errors.
                console.error(e);
            }
            finally
            {
                clearTimeout(timer);

                this.m_fetchingData = false;

                this.loading = false;
                this.detectChanges.emit();
            }
        }
    }

    private async getAll(): Promise<E[]>
    {
        let context: IListContext = {
            batch_offset: 0,
            batch_size  : this.m_batchSize,
            total       : 0
        };

        let result: E[] = [];
        while (context)
        {
            const idResponse    = await this.providerTyped.getList(context);
            const batchResponse = await this.providerTyped.getBatch(idResponse.results);
            result.push(...Array.from(batchResponse.values()));
            context = idResponse.next_list_context;
        }

        return result;
    }

    private async getIds(start: number,
                         pageLength: number,
                         count: number): Promise<K[]>
    {
        let batchSize      = pageLength >= 100 ? pageLength * 2 : pageLength * 3;
        const results: K[] = [];
        if (count)
        {
            // Not first request
            const end          = Math.min(count, start + pageLength);
            const ids          = this.m_all.slice(start, end);
            const invalidIndex = ids.findIndex((id) => !id);
            if (invalidIndex === -1)
            {
                return ids;
            }
            else
            {
                results.push(...ids.slice(start, invalidIndex));
                start += invalidIndex;

                batchSize = Math.min(count - start, batchSize);
                // adjust batchSize until it represents length between first and last invalid id + 1
                while (this.m_all[start + batchSize - 1]) batchSize--;
            }
        }

        const listResponse = await this.providerTyped.getList({
                                                                  batch_size  : batchSize,
                                                                  batch_offset: start,
                                                                  total       : count
                                                              });
        const listResults  = listResponse.results;

        if (!count)
        {
            this.m_count      = listResponse.next_list_context?.total || listResults?.length || 0;
            this.m_all.length = this.m_count;
        }
        this.m_all.splice(start, listResults.length, ...listResults);

        const resultsMissing = Math.min(listResults.length, pageLength - results.length);
        for (let i = 0; i < resultsMissing; i++) results.push(listResults[i]);

        return results;
    }

    public async slice(offset: number,
                       limit: number): Promise<K[]>
    {
        if (limit <= 0) return [];

        return this.getIds(offset, limit, this.count);
    }
}

export interface IDatatableDataProviderBase<T>
{
    wasDestroyed(): boolean;

    getPluralizedItem(): PluralizedValues;

    itemClicked(columnId: string,
                item: T): void;

    getTableConfigId(): string;

    getColumnConfigs(): Promise<ColumnConfiguration[]>;

    setColumnConfigs(configs: ColumnConfiguration[]): Promise<boolean>;
}

export interface IDatatableDataProvider<K, E, T> extends IDatatableDataProviderBase<T>
{
    getList(): Promise<K[]>;

    getPage(offset: number,
            limit: number): Promise<E[]>;

    transform(rows: E[]): Promise<T[]>;
}

export interface GroupByColumnDefinition<G>
{
    id: string,
    groupBy: G,
    ascending: boolean
}

export interface IDatatableGroupingProvider<K, E, T, G = keyof T> extends IDatatableDataProvider<K, E, T>
{
    groupByColumns(): GroupByColumnDefinition<G>[];

    compareByGroupBy(rowA: T,
                     rowB: T,
                     groupBy: G,
                     ascending: boolean): number;
}

export interface ILazyListDatatableProvider<K, E, T> extends IDatatableDataProviderBase<T>
{
    getList(context: IListContext): Promise<IListResponse<K>>;

    getBatch(ids: K[]): Promise<Map<K, E>>;

    transform(rows: E[]): Promise<T[]>;
}

export interface IListContext
{
    total: number;
    batch_size: number;
    batch_offset: number;
}

export interface IListResponse<K>
{
    results: K[];

    next_list_context?: IListContext;
}

export class DatatableSort
{
    prop: string;
    dir: SortDirection;
}

export abstract class DatatableSelectionManager<K, T, ID>
{
    private readonly m_selection: Set<ID> = new Set<ID>();

    private m_all: K[];

    protected get allIDs(): ID[]
    {
        return this.m_all.map((item) => this.keyConverter(item));
    }

    public readonly selectionChange        = new EventEmitter<Set<ID>>();
    public readonly selectionChangeSummary = new EventEmitter<DatatableSelectionChangeSummary<ID>>();

    constructor(protected table: DatatableManagerBase<K, T>,
                public single: boolean,
                public showSelectAll: boolean,
                protected readonly keyConverter: (key: K) => ID,
                protected readonly valueConverter: (value: T) => ID,
                protected readonly m_allowSelection?: (value: ID) => boolean)
    {
    }

    set selection(selection: Set<ID>)
    {
        this.m_selection.clear();

        if (selection)
        {
            for (let value of selection)
            {
                this.m_selection.add(value);
                if (this.single) break;
            }
        }

        this.processCheckedItems();
    }

    get selection(): Set<ID>
    {
        return new Set<ID>(this.m_selection);
    }

    allowSelectionByRow(row: T): boolean
    {
        if (!this.m_allowSelection) return true;
        return this.m_allowSelection(this.valueConverter(row));
    }

    allowSelectionByKey(key: K): boolean
    {
        if (!this.m_allowSelection) return true;
        return this.m_allowSelection(this.keyConverter(key));
    }

    async update()
    {
        this.m_all = await this.table.slice(0, this.table.count);
        this.processCheckedItems();
    }

    //--//

    private m_allChecked: boolean = false;
    get allChecked(): boolean
    {
        return this.m_allChecked;
    }

    set allChecked(value: boolean)
    {
        this.m_allChecked = value;
        this.checkAllItems();
    }

    //--//

    private m_indeterminateChecked: boolean = false;
    get indeterminateChecked(): boolean
    {
        return this.m_indeterminateChecked;
    }

    set indeterminateChecked(value: boolean)
    {
        this.m_indeterminateChecked = value;
    }

    isChecked(row: T): boolean
    {
        let id = this.valueConverter(row);
        return this.m_selection.has(id);
    }

    setChecked(row: T,
               checked: boolean)
    {
        if (!this.allowSelectionByRow(row)) return;

        if (this.single)
        {
            this.m_selection.clear();
        }

        let id = this.valueConverter(row);
        if (checked)
        {
            this.m_selection.add(id);
        }
        else
        {
            this.m_selection.delete(id);
        }
    }

    itemChecked(row: T)
    {
        this.handleItemChecked(row);
        this.processCheckedItems();

        let id: ID    = this.valueConverter(row);
        let checked   = this.isChecked(row);
        let selection = this.selection;
        let summary   = new DatatableSelectionChangeSummary(DatatableSelectionTargetType.INDIVIDUAL, id, checked, selection);

        this.selectionChange.emit(selection);
        this.selectionChangeSummary.emit(summary);
    }

    abstract handleItemChecked(row: T): void;

    protected checkAllItems()
    {
        if (!this.m_all) return;

        let toSelect: ID[] = null;
        if (this.allChecked)
        {
            toSelect = [];
            for (const item of this.m_all)
            {
                if (this.allowSelectionByKey(item))
                {
                    toSelect.push(this.keyConverter(item));
                }
            }
        }

        this.checkAllItemsHelper(toSelect);
    }

    protected checkAllItemsHelper(toSelect: ID[])
    {
        // Empty or fill selection
        if (toSelect)
        {
            for (const id of toSelect) this.m_selection.add(id);
        }
        else
        {
            this.m_selection.clear();
        }

        // Update states
        this.processCheckedItems();

        let selection = this.selection;
        let summary   = new DatatableSelectionChangeSummary(DatatableSelectionTargetType.ALL, null, !!toSelect, selection);
        this.selectionChange.emit(selection);
        this.selectionChangeSummary.emit(summary);
    }

    reset()
    {
        this.m_selection.clear();
        this.m_indeterminateChecked = false;
        this.m_allChecked           = false;
    }

    private processCheckedItems()
    {
        if (!this.m_all) return;
        let allChecked = true;
        let anyChecked = false;
        for (let item of this.m_all)
        {
            if (this.m_selection.has(this.keyConverter(item)))
            {
                anyChecked = true;
            }
            else
            {
                allChecked = false;
            }
        }

        this.m_indeterminateChecked = anyChecked && !allChecked;
        this.m_allChecked           = anyChecked;
    }

    public static ensureSelection<T>(selectionManager: DatatableSelectionManager<any, any, T>,
                                     selection: T)
    {
        let selected = selectionManager.selection;
        selected.add(selection);
        selectionManager.selection = selected;
    }
}

export class SimpleSelectionManager<K, T, ID> extends DatatableSelectionManager<K, T, ID>
{
    public handleItemChecked(row: T): void
    {
        // Modify selection state
        if (!this.single)
        {
            this.setChecked(row, !this.isChecked(row));
        }
        else
        {
            this.setChecked(row, true);
        }
    }
}

export class LazySimpleSelectionManager<K, T, ID> extends SimpleSelectionManager<K, T, ID>
{
    constructor(table: LazyListDatatableManager<K, any, T>,
                single: boolean,
                showSelectAll: boolean,
                keyConverter: (key: K) => ID,
                valueConverter: (value: T) => ID,
                protected filterSelectableItems?: (allIds: ID[]) => Promise<ID[]>,
                allowSelection?: (value: ID) => boolean)
    {
        super(table, single, showSelectAll, keyConverter, valueConverter, allowSelection);
    }

    protected async getSelectableItems(): Promise<ID[]>
    {
        const ids = this.allIDs;
        return this.filterSelectableItems?.(ids) || ids;
    }

    protected async checkAllItems()
    {
        let selectable = null;
        if (this.allChecked)
        {
            selectable = await this.getSelectableItems();
            if (!selectable) return;
        }

        this.checkAllItemsHelper(selectable);
    }
}

export class DatatableSelectionChangeSummary<T>
{
    constructor(public type: DatatableSelectionTargetType,
                public target: T,
                public state: boolean,
                public selection: Set<T>)
    {}
}

export enum DatatableSelectionTargetType
{
    ALL, INDIVIDUAL
}

export abstract class DatatableExpansionManager<K, T, ID>
{
    private readonly m_expanded: Map<ID, DatatableExpansion<ID>> = new Map<ID, DatatableExpansion<ID>>();

    private m_all: K[];

    public readonly expandedChange        = new EventEmitter<Set<DatatableExpansion<ID>>>();
    public readonly expandedChangeSummary = new EventEmitter<DatatableExpansionChangeSummary<DatatableExpansion<ID>>>();

    constructor(protected table: DatatableManagerBase<K, T>,
                public single: boolean,
                public showExpandAll: boolean,
                protected readonly keyConverter: (key: K) => ID,
                protected readonly valueConverter: (value: T) => ID)
    {
    }

    set expanded(expanded: Set<DatatableExpansion<ID>>)
    {
        this.m_expanded.clear();

        if (expanded)
        {
            for (let value of expanded)
            {
                this.m_expanded.set(value.id, value);
                if (this.single) break;
            }
        }

        this.processExpandedItems();
    }

    get expanded(): Set<DatatableExpansion<ID>>
    {
        return new Set<DatatableExpansion<ID>>(this.m_expanded.values());
    }

    async update()
    {
        this.m_all = await this.table.slice(0, this.table.count);
        this.processExpandedItems();
    }

    //--//

    get anyExpanded(): boolean
    {
        return this.m_expanded.size > 0;
    }

    private m_allExpanded: boolean = false;
    get allExpanded(): boolean
    {
        return this.m_allExpanded;
    }

    set allExpanded(value: boolean)
    {
        this.m_allExpanded = value;
        this.expandAllItems(value, null);
    }

    //--//

    private m_indeterminateExpanded: boolean = false;
    get indeterminateExpanded(): boolean
    {
        return this.m_indeterminateExpanded;
    }

    set indeterminateExpanded(value: boolean)
    {
        this.m_indeterminateExpanded = value;
    }

    isExpanded(row: T): boolean
    {
        if (this.canExpand(row))
        {
            let id = this.valueConverter(row);
            return this.m_expanded.has(id);
        }

        return false;
    }

    getTrigger(row: T): string
    {
        if (this.isExpanded(row))
        {
            let id = this.valueConverter(row);
            return this.m_expanded.get(id).trigger;
        }

        return null;
    }

    setExpanded(row: T,
                trigger: string,
                expanded: boolean)
    {
        if (this.single)
        {
            this.m_expanded.clear();
        }

        let id = this.valueConverter(row);
        if (expanded)
        {
            let expansion = new DatatableExpansion(id, trigger);
            this.m_expanded.set(id, expansion);
        }
        else
        {
            this.m_expanded.delete(id);
        }
    }

    itemExpanded(row: T,
                 trigger: string)
    {
        this.handleItemExpanded(row, trigger);
        this.processExpandedItems();

        let expansion  = this.m_expanded.get(this.valueConverter(row));
        let expanded   = this.isExpanded(row);
        let expansions = this.expanded;
        let summary    = new DatatableExpansionChangeSummary(DatatableExpansionTargetType.INDIVIDUAL, expansion, expanded, expansions);

        this.expandedChange.emit(expansions);
        this.expandedChangeSummary.emit(summary);
    }

    abstract handleItemExpanded(row: T,
                                trigger: string): void;

    abstract canExpand(row: T): boolean;

    expandAllItems(state: boolean,
                   trigger: string)
    {
        if (!this.m_all) return;

        // Empty or fill selection
        if (state)
        {
            for (let item of this.m_all)
            {
                let id        = this.keyConverter(item);
                let expansion = new DatatableExpansion(id, trigger);
                this.m_expanded.set(id, expansion);
            }
        }
        else
        {
            this.m_expanded.clear();
        }

        // Update states
        this.processExpandedItems();

        let expansions = this.expanded;
        let summary    = new DatatableExpansionChangeSummary(DatatableExpansionTargetType.ALL, null, state, expansions);
        this.expandedChange.emit(expansions);
        this.expandedChangeSummary.emit(summary);
    }

    private processExpandedItems()
    {
        if (!this.m_all) return;

        let allExpanded = true;
        let anyExpanded = false;
        for (let item of this.m_all)
        {
            if (this.m_expanded.has(this.keyConverter(item)))
            {
                anyExpanded = true;
            }
            else
            {
                allExpanded = false;
            }
        }

        this.m_indeterminateExpanded = anyExpanded && !allExpanded;
        this.m_allExpanded           = anyExpanded;
    }
}

export class SimpleExpansionManager<K, T, ID> extends DatatableExpansionManager<K, T, ID>
{
    public handleItemExpanded(row: T,
                              trigger: string): void
    {
        // Modify selection state
        if (this.canExpand(row))
        {
            this.setExpanded(row, trigger, !this.isExpanded(row));
        }

    }

    public canExpand(row: T): boolean
    {
        return true;
    }
}

export class DatatableExpansionChangeSummary<T>
{
    constructor(public type: DatatableExpansionTargetType,
                public target: T,
                public state: boolean,
                public selection: Set<T>)
    {}
}

export class DatatableExpansion<ID>
{
    constructor(public id: ID,
                public trigger: string)
    {}
}

export enum DatatableExpansionTargetType
{
    ALL, INDIVIDUAL
}

export class ColumnConfiguration
{
    private static readonly resizeGripperWidthPx: number   = 1;
    private static readonly resizeGripperPaddingPx: number = 8;

    public template: DatatableColumnDirective<any>;

    public headerElement: HTMLElement;
    public gripperElement: HTMLElement;
    public cellElements: HTMLElement[];

    public hasGripper: boolean = true;

    public leftOffset: number;
    public minWidth: number;
    public flexBasis: number;

    get activeMinWidth(): number
    {
        return this.minWidth || this.template?.minWidth;
    }

    public prevColConfig: ColumnConfiguration;
    public nextColConfig: ColumnConfiguration;

    public scratchPadForResize: number;

    private originalGripperOffset: number;
    private originalLeftOffset: number;

    private get gripperOffset(): number
    {
        const offset = ColumnConfiguration.resizeGripperPaddingPx + ColumnConfiguration.resizeGripperWidthPx;
        if (this.headerElement && this.template?.sortable)
        {
            if (this.originalGripperOffset) return this.originalGripperOffset - (this.leftOffset - this.originalLeftOffset);

            // sortable header cells have a different structure and an internal element with position relative for the sort arrow
            const headerPadding = UtilsService.parseComputedStyle(getComputedStyle(this.headerElement).paddingLeft, "px");
            return this.headerElement.clientWidth - headerPadding - (offset + 1);
        }

        if (this.originalGripperOffset) return this.originalGripperOffset;
        return this.leftOffset + this.flexBasis - offset;
    }

    constructor(public readonly id: string,
                public enabled: boolean,
                public name?: string)
    {
        this.name ||= "";
    }

    public static newInstance(template: DatatableColumnDirective<any>): ColumnConfiguration
    {
        const config    = new ColumnConfiguration(template.id, template.enabled, template.name);
        config.template = template;
        return config;
    }

    resizePrep()
    {
        this.originalGripperOffset = this.gripperOffset;
        this.originalLeftOffset    = this.leftOffset;
    }

    completeResize()
    {
        this.originalLeftOffset    = undefined;
        this.originalGripperOffset = undefined;
    }

    updateColumnStyles()
    {
        if (this.headerElement && this.cellElements)
        {
            const flexBasisStyle = UtilsService.formatCssValue(this.flexBasis);
            const minWidthStyle  = UtilsService.formatCssValue(this.activeMinWidth);

            this.headerElement.style.flexBasis = flexBasisStyle;
            this.headerElement.style.minWidth  = minWidthStyle;

            for (let cellElem of this.cellElements)
            {
                cellElem.style.flexBasis = flexBasisStyle;
                cellElem.style.minWidth  = minWidthStyle;
            }

            this.updateGripperPosition();
        }
    }

    updateGripperPosition()
    {
        if (this.hasGripper && this.gripperElement)
        {
            this.gripperElement.style.left = UtilsService.formatCssValue(this.gripperOffset);
        }
    }

    copy(other: ColumnConfiguration)
    {
        if (this.id === other.id)
        {
            this.enabled = other.enabled;
            this.name    = other.name;
        }
    }

    clone(): ColumnConfiguration
    {
        const clone    = new ColumnConfiguration(this.id, this.enabled, this.name);
        clone.template = this.template;
        return clone;
    }

    isEquivalent(other: ColumnConfiguration)
    {
        if (this.id != other.id) return false;
        if (this.enabled !== other.enabled) return false;
        return !this.name || this.name === other.name;
    }

    moveCursor(toRight: boolean): ColumnConfiguration
    {
        return toRight ? this.nextColConfig : this.prevColConfig;
    }

    public static configsAreEquivalent(configsA: ColumnConfiguration[],
                                       configsB: ColumnConfiguration[]): boolean
    {
        return UtilsService.arraysEquivalent(configsA, configsB, (configA,
                                                                  configB) => configA.isEquivalent(configB));
    }
}

//--//

export class FilterDebouncer<T, F>
{
    private m_currentValue: T;
    private m_nextValue: T;
    private m_filters: F;
    private m_filtersExternal: F;
    private m_debouncer: number;

    constructor(private allocateState: () => T,
                private restoreState: () => T,
                private saveState: (val: T) => void,
                private convertToFilter: (state: T,
                                          externalFilters: F) => F,
                private notifyChanges: (filtersChanged: boolean) => void)
    {
    }

    public generateFilter(): F
    {
        if (this.m_nextValue)
        {
            this.m_currentValue = this.m_nextValue;
            this.cancelDebouncer();
        }

        this.ensureState();

        this.saveState(this.m_currentValue);

        return this.convertToFilter(this.m_currentValue, this.m_filtersExternal);
    }

    public getExternalFilter(): F
    {
        return this.m_filtersExternal;
    }

    public setExternalFilter(val: F): void
    {
        if (!UtilsService.compareJson(this.m_filtersExternal, val))
        {
            this.m_filtersExternal = val;
            this.debounce();
        }
    }

    public getProperty<K extends keyof T>(prop: K): T[K]
    {
        this.ensureState();

        let obj = this.m_nextValue || this.m_currentValue;

        return obj[prop];
    }

    public setProperty<K extends keyof T>(prop: K,
                                          val: T[K]): void
    {
        this.ensureState();

        if (!this.m_nextValue)
        {
            this.m_nextValue = Object.assign(this.allocateState(), this.m_currentValue);
        }

        this.m_nextValue[prop] = val;

        this.debounce();
    }

    private debounce()
    {
        this.cancelDebouncer();

        this.m_debouncer = window.setTimeout(() =>
                                      {
                                          this.m_debouncer = undefined;

                                          let filters = this.generateFilter();
                                          let changed = this.m_filters && !UtilsService.compareJson(filters, this.m_filters);

                                          this.m_filters = filters;
                                          this.notifyChanges(changed);
                                      }, 100);
    }

    private cancelDebouncer()
    {
        if (this.m_debouncer)
        {
            clearTimeout(this.m_debouncer);
        }
    }

    private ensureState()
    {
        if (!this.m_currentValue)
        {
            this.m_currentValue = this.restoreState() || this.allocateState();
        }
    }
}
